/**
 * Camera detail page — overview, snapshots, maintenance log.
 *
 * Surfaces:
 *   - Header card: name + status pill + last-ping summary + key buttons
 *     (Capture snapshot, Test ping, Reveal credentials, Live view link).
 *   - Tabs: Overview / Snapshots / Maintenance
 *   - Snapshots tab: grid of recent snapshots (thumbnail via /image endpoint
 *     with bearer-token-aware fetch), reason badge, captured-by, delete.
 *   - Maintenance tab: editable notes + warranty + storage_days + install_at
 *     (delegates the actual edit to the parent dialog via "Edit" button).
 */
import { useEffect, useState } from 'react';
import { useParams, useNavigate } from '@tanstack/react-router';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  ArrowLeft,
  Camera as CameraIcon,
  CameraOff,
  RefreshCw,
  Trash2,
  AlertOctagon
} from 'lucide-react';
import { toast } from 'sonner';
import { get, post, del, getErrorMessage } from '@/lib/api';
import { PageHeader } from '@/components/layout/PageHeader';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Skeleton } from '@/components/ui/skeleton';
import { Badge } from '@/components/ui/badge';
import { EmptyState } from '@/components/ui/empty-state';
import { TabsPills } from '@/components/ui/tabs-pills';
import { CursorPaginator } from '@/components/ui/cursor-paginator';
import { ConfirmDialog } from '@/components/ui/confirm-dialog';
import { useAuthStore } from '@/lib/auth';
import { formatDateTime, formatRelative } from '@/lib/format';
import type { Camera } from '@/routes/cameras';

const STATUS_VARIANT: Record<Camera['status'], 'ok' | 'alert' | 'warn' | 'muted'> = {
  active: 'ok',
  offline: 'alert',
  maintenance: 'warn',
  decommissioned: 'muted'
};

interface Snapshot {
  id: string;
  camera_id: string;
  storage_key: string;
  captured_at: string;
  captured_by_user_id: string | null;
  capture_reason: string;
  notes: string | null;
}

interface CursorList<T> {
  items: T[];
  next_cursor: string | null;
}

const API_URL = import.meta.env.VITE_API_URL ?? 'http://localhost:4100';
const API_PREFIX = import.meta.env.VITE_API_PREFIX ?? '/v1';

export function CameraDetailPage(): JSX.Element {
  const params = useParams({ strict: false }) as { id: string };
  const navigate = useNavigate();
  const qc = useQueryClient();
  const [tab, setTab] = useState<'overview' | 'snapshots' | 'maintenance'>('overview');
  const [cursor, setCursor] = useState<string | null>(null);
  const [deleteSnap, setDeleteSnap] = useState<Snapshot | null>(null);

  const camQuery = useQuery({
    queryKey: ['camera', params.id],
    queryFn: () => get<Camera>(`/cameras/${params.id}`)
  });
  const camera = camQuery.data;

  const snapsQuery = useQuery({
    queryKey: ['camera-snapshots', params.id, cursor],
    queryFn: () => {
      const qs = new URLSearchParams({ camera_id: params.id, limit: '24' });
      if (cursor) qs.set('cursor', cursor);
      return get<CursorList<Snapshot>>(`/camera-snapshots?${qs.toString()}`);
    },
    enabled: tab === 'snapshots'
  });

  const pingMutation = useMutation({
    mutationFn: () => post<{ ok: boolean; ms: number }>(`/cameras/${params.id}/ping`, {}),
    onSuccess: (data) => {
      toast.success(data.ok ? `Ping OK (${data.ms} ms)` : `Ping failed (${data.ms} ms)`);
      void qc.invalidateQueries({ queryKey: ['camera', params.id] });
    },
    onError: (e) => toast.error(getErrorMessage(e))
  });

  const snapshotMutation = useMutation({
    mutationFn: () =>
      post<{ id: string }>(`/cameras/${params.id}/snapshot`, { capture_reason: 'manual' }),
    onSuccess: () => {
      toast.success('Snapshot captured');
      void qc.invalidateQueries({ queryKey: ['camera-snapshots', params.id] });
    },
    onError: (e) => toast.error(getErrorMessage(e))
  });

  const deleteSnapMutation = useMutation({
    mutationFn: (id: string) => del<void>(`/camera-snapshots/${id}`),
    onSuccess: () => {
      toast.success('Snapshot deleted');
      setDeleteSnap(null);
      void qc.invalidateQueries({ queryKey: ['camera-snapshots', params.id] });
    },
    onError: (e) => toast.error(getErrorMessage(e))
  });

  if (camQuery.isLoading) {
    return (
      <PageHeader title="Camera">
        <Skeleton className="h-32 w-full" />
      </PageHeader>
    );
  }
  if (camQuery.isError || !camera) {
    return (
      <PageHeader title="Camera">
        <Card>
          <CardContent className="text-vt-alert">
            {camQuery.error ? getErrorMessage(camQuery.error) : 'Not found'}
          </CardContent>
        </Card>
      </PageHeader>
    );
  }

  return (
    <PageHeader
      title={camera.name}
      description={camera.location_label ?? undefined}
      actions={
        <div className="flex gap-vt-2">
          <Button variant="ghost" onClick={() => navigate({ to: '/cameras' })}>
            <ArrowLeft className="h-4 w-4" />
            Back
          </Button>
          <Button onClick={() => snapshotMutation.mutate()} disabled={snapshotMutation.isPending}>
            <CameraIcon className="h-4 w-4" />
            Capture snapshot now
          </Button>
        </div>
      }
    >
      {/* Header card */}
      <Card className="mb-vt-4">
        <CardContent className="flex flex-col gap-vt-3 p-vt-4 sm:flex-row sm:items-center sm:justify-between">
          <div className="flex flex-col gap-vt-2">
            <div className="flex items-center gap-vt-2">
              <Badge variant={STATUS_VARIANT[camera.status]}>{camera.status}</Badge>
              {camera.consecutive_ping_failures >= 2 && camera.status !== 'offline' ? (
                <Badge variant="warn">
                  <AlertOctagon className="h-3 w-3" />
                  {camera.consecutive_ping_failures} failed pings
                </Badge>
              ) : null}
              {camera.has_credentials ? <Badge variant="info">Credentials stored</Badge> : null}
            </div>
            <div className="text-vt-12 text-vt-text-muted">
              Last ping:{' '}
              {camera.last_ping_at
                ? `${formatRelative(camera.last_ping_at)} · ${camera.last_ping_ok ? 'OK' : 'failed'}`
                : '—'}
            </div>
          </div>
          <div className="flex flex-wrap gap-vt-2">
            <Button variant="ghost" onClick={() => pingMutation.mutate()} disabled={pingMutation.isPending}>
              <RefreshCw className="h-4 w-4" />
              Test ping
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Tabs */}
      <TabsPills
        aria-label="Camera section"
        value={tab}
        onChange={(v) => setTab(v as typeof tab)}
        options={[
          { value: 'overview', label: 'Overview' },
          { value: 'snapshots', label: 'Snapshots' },
          { value: 'maintenance', label: 'Maintenance' }
        ]}
      />

      <div className="mt-vt-4">
        {tab === 'overview' ? (
          <OverviewTab camera={camera} />
        ) : tab === 'snapshots' ? (
          <SnapshotsTab
            cameraId={camera.id}
            isLoading={snapsQuery.isLoading}
            isError={snapsQuery.isError}
            items={snapsQuery.data?.items ?? []}
            nextCursor={snapsQuery.data?.next_cursor ?? null}
            onLoadMore={(c) => setCursor(c)}
            onDelete={(s) => setDeleteSnap(s)}
          />
        ) : (
          <MaintenanceTab camera={camera} />
        )}
      </div>

      <ConfirmDialog
        open={Boolean(deleteSnap)}
        onOpenChange={(o) => !o && setDeleteSnap(null)}
        title="Delete snapshot?"
        description="This snapshot file will be removed from storage and the database."
        confirmLabel="Delete"
        variant="danger"
        onConfirm={() => {
          if (deleteSnap) deleteSnapMutation.mutate(deleteSnap.id);
        }}
        loading={deleteSnapMutation.isPending}
      />
    </PageHeader>
  );
}

function OverviewTab({ camera }: { camera: Camera }): JSX.Element {
  return (
    <div className="grid grid-cols-1 gap-vt-3 lg:grid-cols-2">
      <Card>
        <CardContent className="p-vt-4">
          <div className="mb-vt-2 text-vt-12 font-semibold uppercase tracking-wide text-vt-text-soft">
            Hardware
          </div>
          <Row label="Type" value={camera.camera_type} />
          <Row label="Brand" value={camera.brand ?? '—'} />
          <Row label="Model" value={camera.model ?? '—'} />
          <Row label="Resolution" value={camera.resolution ?? '—'} />
          <Row label="PTZ" value={camera.has_ptz ? 'Yes' : 'No'} />
          <Row label="Audio" value={camera.has_audio ? 'Yes' : 'No'} />
          <Row label="Night vision" value={camera.has_night_vision ? 'Yes' : 'No'} />
        </CardContent>
      </Card>
      <Card>
        <CardContent className="p-vt-4">
          <div className="mb-vt-2 text-vt-12 font-semibold uppercase tracking-wide text-vt-text-soft">
            Stream
          </div>
          <Row label="Stream URL" value={camera.stream_url ? maskUrl(camera.stream_url) : '—'} />
          <Row label="Snapshot URL" value={camera.snapshot_url ? maskUrl(camera.snapshot_url) : '—'} />
          <Row label="Credentials" value={camera.has_credentials ? 'Stored (encrypted)' : 'None'} />
          <Row label="Recording" value={camera.recording_enabled ? 'Enabled' : 'Disabled'} />
          <Row label="Storage" value={camera.storage_days != null ? `${camera.storage_days} days` : '—'} />
          <Row label="NVR channel" value={camera.nvr_channel_no != null ? String(camera.nvr_channel_no) : '—'} />
        </CardContent>
      </Card>
    </div>
  );
}

function MaintenanceTab({ camera }: { camera: Camera }): JSX.Element {
  return (
    <Card>
      <CardContent className="p-vt-4">
        <div className="grid grid-cols-1 gap-vt-3 sm:grid-cols-2">
          <Row label="Installed" value={camera.installed_at ? formatDateTime(camera.installed_at) : '—'} />
          <Row label="Warranty until" value={camera.warranty_until ? formatDateTime(camera.warranty_until) : '—'} />
          <Row label="Created" value={formatDateTime(camera.created_at)} />
          <Row label="Updated" value={formatDateTime(camera.updated_at)} />
        </div>
        {camera.notes ? (
          <div className="mt-vt-4 rounded-vt-sm bg-vt-surface-2 p-vt-3 text-vt-13">{camera.notes}</div>
        ) : null}
      </CardContent>
    </Card>
  );
}

function SnapshotsTab({
  isLoading,
  isError,
  items,
  nextCursor,
  onLoadMore,
  onDelete
}: {
  cameraId: string;
  isLoading: boolean;
  isError: boolean;
  items: Snapshot[];
  nextCursor: string | null;
  onLoadMore: (cursor: string) => void;
  onDelete: (s: Snapshot) => void;
}): JSX.Element {
  if (isLoading) {
    return (
      <div className="grid grid-cols-1 gap-vt-3 sm:grid-cols-2 lg:grid-cols-4">
        {Array.from({ length: 8 }).map((_, i) => (
          <Skeleton key={i} className="h-44 w-full" />
        ))}
      </div>
    );
  }
  if (isError) {
    return (
      <Card>
        <CardContent className="text-vt-alert">Failed to load snapshots</CardContent>
      </Card>
    );
  }
  if (items.length === 0) {
    return (
      <Card>
        <CardContent>
          <EmptyState
            icon={<CameraOff className="h-7 w-7" />}
            title="No snapshots yet"
            description="Capture an evidence snapshot to start building the camera's history."
          />
        </CardContent>
      </Card>
    );
  }
  return (
    <>
      <div className="grid grid-cols-1 gap-vt-3 sm:grid-cols-2 lg:grid-cols-4">
        {items.map((s) => (
          <SnapshotCard key={s.id} snapshot={s} onDelete={() => onDelete(s)} />
        ))}
      </div>
      <CursorPaginator
        hasMore={Boolean(nextCursor)}
        loading={false}
        itemsShown={items.length}
        onLoadMore={() => nextCursor && onLoadMore(nextCursor)}
      />
    </>
  );
}

function SnapshotCard({
  snapshot,
  onDelete
}: {
  snapshot: Snapshot;
  onDelete: () => void;
}): JSX.Element {
  // Stream snapshot via auth-gated /image endpoint. We need to send the
  // Bearer token; the easiest route is a fetch + blob URL we render in <img>.
  const [src, setSrc] = useState<string | null>(null);
  const token = useAuthStore.getState().accessToken;
  useEffect(() => {
    let cancelled = false;
    let blobUrl: string | null = null;
    void (async () => {
      try {
        const res = await fetch(`${API_URL}${API_PREFIX}/camera-snapshots/${snapshot.id}/image`, {
          headers: token ? { Authorization: `Bearer ${token}` } : {}
        });
        if (!res.ok) throw new Error(`HTTP ${res.status}`);
        const blob = await res.blob();
        if (cancelled) return;
        blobUrl = URL.createObjectURL(blob);
        setSrc(blobUrl);
      } catch {
        if (!cancelled) setSrc(null);
      }
    })();
    return () => {
      cancelled = true;
      if (blobUrl) URL.revokeObjectURL(blobUrl);
    };
  }, [snapshot.id, token]);

  return (
    <Card>
      <CardContent className="flex flex-col gap-vt-2 p-vt-3">
        <div className="aspect-video w-full overflow-hidden rounded-vt-sm bg-vt-surface-2">
          {src ? (
            <img src={src} alt={`Snapshot ${snapshot.id}`} className="h-full w-full object-cover" />
          ) : (
            <div className="flex h-full w-full items-center justify-center text-vt-text-muted">
              <CameraIcon className="h-6 w-6" />
            </div>
          )}
        </div>
        <div className="flex items-start justify-between gap-vt-2">
          <div className="min-w-0">
            <div className="truncate text-vt-12 text-vt-text">
              {formatDateTime(snapshot.captured_at)}
            </div>
            <Badge variant="muted" className="mt-1">
              {snapshot.capture_reason}
            </Badge>
          </div>
          <Button size="sm" variant="ghost" onClick={onDelete} className="text-vt-alert">
            <Trash2 className="h-3.5 w-3.5" />
          </Button>
        </div>
      </CardContent>
    </Card>
  );
}

function Row({ label, value }: { label: string; value: React.ReactNode }): JSX.Element {
  return (
    <div className="flex items-center justify-between gap-vt-2 border-b border-vt-border py-vt-2 last:border-b-0">
      <span className="text-vt-12 text-vt-text-muted">{label}</span>
      <span className="text-vt-13 text-vt-text">{value}</span>
    </div>
  );
}

/** Visually mask the userinfo portion of a URL (we never round-trip creds to the UI). */
function maskUrl(url: string): string {
  return url.replace(/^([a-z]+:\/\/)([^@]+@)/, '$1***@');
}
